<template>
  <div :class="['util-input', size]">
    <div v-if="showLabel" class="label">
      <span>{{ label }}</span>
      <slot name="label-extra" />
    </div>

    <div :class="['input-container', isDisabled ? 'input-disabled' : '']" :style="inputContainerStyle">
      <img v-if="iconLink" :src="iconLink" alt="icon" class="icon" />

      <template v-if="isTextArea">
        <textarea
          :rows="isMiniTextArea ? 4 : 8"
          :placeholder="placeholder"
          :value="inputValue"
          class="textarea"
          :disabled="isDisabled"
          @input="$emit('update:value', $event.target.value)"
        ></textarea>
      </template>

      <template v-else-if="isDropDown">
        <select
          :value="inputValue"
          class="dropdown"
          @change="$emit('update:value', $event.target.value)"
          :disabled="isDisabled"
        >
          <option v-if="showDropdownDefault" value="" disabled>{{ placeholder }}</option>
          <option v-for="(option, index) in dropdownOptions" :key="index" :value="option.selectionValue">
            {{ $t(option.label) }}
          </option>
        </select>
      </template>

      <template v-else-if="isCheckBox">
        <div class="checkbox-group">
          <input
            type="checkbox"
            :id="label"
            :checked="inputValue"
            @change="$emit('update:value', $event.target.checked)"
            class="checkbox"
            :disabled="isDisabled"
          />
          <label :for="label" class="checkbox-label">{{ placeholder }}</label>
        </div>
      </template>

       <template v-else-if="isFileUpload">
        <div class="file-upload">
          <!-- When NO file selected: show Choose File button + placeholder -->
          <template v-if="!hasSelectedFile">
            <label class="file-button">
              {{ buttonText }}
              <input type="file" :accept="accept" :disabled="isDisabled" hidden @change="handleFileChange" />
            </label>

            <div v-if="showFileName" class="file-name">
              {{ placeholder }}
            </div>
          </template>

          <!-- When file selected: hide Choose File, show filename + remove X -->
          <template v-else>
            <div v-if="showFileName" class="file-name">
              {{ fileName || existingImageLabel }}
            </div>

            <button
              type="button"
              class="file-remove"
              :disabled="isDisabled"
              aria-label="Remove selected file"
              @click="removeFile"
            >
              ×
            </button>
          </template>
        </div>
      </template>
      
      <template v-else>
        <input
          :type="computedInputType"
          :value="inputValue"
          :placeholder="placeholder"
          @keypress="validateKeyPress"
          @input="handleInput($event)"
          :disabled="isDisabled"
          class="text-input"
        />
      </template>

      <img
        v-if="inputType === 'password' && showPasswordToggle"
        :src="passwordVisible ? secretHideIcon : secretVisibleIcon"
        @click="passwordVisible = !passwordVisible"
        class="password-visibility-toggle"
        alt="toggle password visibility"
      />
    </div>

    <!-- ✅ Preview below input -->
    <div v-if="isFileUpload && (effectivePreviewUrl || previewError)" class="file-preview">
      <img v-if="previewError && fallbackPreviewIcon" :src="fallbackPreviewIcon" alt="upload failed" />
      <img v-else :src="effectivePreviewUrl" alt="preview" />
    </div>

    <div class="helper-text" v-if="helperText">{{ helperText }}</div>
  </div>
</template>

<script>
import SecretVisibleIcon from "@/assets/icons/secret-visible.svg";
import SecretHideIcon from "@/assets/icons/secret-hide.svg";
import imageCompression from "browser-image-compression";
import { toast } from "vue3-toastify";

export default {
  name: "UtilInput",
  props: {
    label: { type: String, default: "Label" },
    value: { type: [String, Number, Boolean, Object, null], default: null },
    placeholder: { type: String, default: "" },
    helperText: { type: String, default: "" },
    iconLink: { type: String, required: false },

    isTextArea: { type: Boolean, default: false },
    isMiniTextArea: { type: Boolean, default: false },
    isDropDown: { type: Boolean, default: false },
    isCheckBox: { type: Boolean, default: false },

    dropdownOptions: { type: Array, default: () => [] },
    showDropdownDefault: { type: Boolean, default: true },
    showLabel: { type: Boolean, default: true },
    isDisabled: { type: Boolean, default: false },

    isFileUpload: { type: Boolean, default: false },
    accept: { type: String, default: "" },          // e.g. "image/png,image/jpeg"
    buttonText: { type: String, default: "Choose File" },
    showFileName: { type: Boolean, default: true },
    previewError: { type: Boolean, default: false },
    fallbackPreviewIcon: { type: String, default: "" },
    previewUrl: { type: String, default: "" },

    enableImageCompression: { type: Boolean, default: false }, // opt-in
    maxSizeMB: { type: Number, default: 2.0 },
    maxWidthOrHeight: { type: Number, default: 1920 },
    initialQuality: { type: Number, default: 0.8 },
    hardLimitMB: { type: Number, default: Number(process.env.VUE_APP_IMAGE_FRONTEND_HARD_LIMIT_MB ?? 7) },

    size: {
      type: String,
      default: "md",
      validator: (v) => ["xs", "sm", "md", "lg"].includes(v),
    },

    boxBg: { type: String, default: "#ededed" },

    inputType: {
      type: String,
      default: "text",
      validator: (v) => ["text", "email", "password", "number"].includes(v),
    },

    showPasswordToggle: { type: Boolean, default: true },
  },
  data() {
    return {
      passwordVisible: false,
      secretVisibleIcon: SecretVisibleIcon,
      secretHideIcon: SecretHideIcon,
      fileName: "",
      internalPreviewUrl: "",
    };
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("update:value", v);
      },
    },
    computedInputType() {
      return this.inputType === "password" && this.showPasswordToggle
        ? this.passwordVisible
          ? "text"
          : "password"
        : this.inputType;
    },
    // Clean inline style: omit height when not a textarea (avoid invalid 'default')
    inputContainerStyle() {
      const style = {
        backgroundColor: this.boxBg,
      };
      if (this.isTextArea) {
        style.height = this.isMiniTextArea ? "150px" : "256px";
      } else if (this.isCheckBox) {
        style.height = "auto";
        style.paddingTop = "10px";
        style.paddingBottom = "10px";
        style.minHeight = "var(--in-h)";
      }
      return style;
    },
    effectivePreviewUrl() {
      // prefer parent previewUrl, fallback to internal blob preview
      return this.previewUrl || this.internalPreviewUrl || "";
    },
    hasSelectedFile() {
      return (!!this.value && typeof this.value === "object") || !!this.effectivePreviewUrl;
    },
    existingImageLabel() {
      const hasFileObject = this.value && typeof this.value === "object";
      const hasPreview = !!this.effectivePreviewUrl;

      // Old image exists (previewUrl), but user didn't pick a new file
      if (hasPreview && !hasFileObject) return "Existing Image";

      return "";
    },
  },
  watch: {  
    value(newVal) {
      if (!newVal) this.fileName = "";
    },
  },
  beforeUnmount() {
    this.revokeBlobPreview();
  },
  methods: {
    handleInput(event) {
      let newValue = event.target.value;
      if (this.inputType === "number") newValue = newValue.replace(/[^0-9]/g, "");
      this.$emit("update:value", newValue);
    },
    validateKeyPress(event) {
      if (this.inputType === "number" && !/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    },
    revokeBlobPreview() {
      const url = this.internalPreviewUrl;
      if (url && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
      this.internalPreviewUrl = "";
    },

    async handleFileChange(e) {
      const file = e?.target?.files?.[0];
      if (!file) return;

      // allow selecting same file again
      e.target.value = "";

      this.fileName = file.name;

      // clear old internal blob preview (do NOT touch parent previewUrl here)
      this.revokeBlobPreview();

      // default behavior: no compression, just emit file
      if (!this.enableImageCompression) {
        this.$emit("update:value", file);

        const previewUrl = URL.createObjectURL(file);
        this.internalPreviewUrl = previewUrl;
        this.$emit("update:previewUrl", previewUrl);

        return;
      }

      const sizeMB = file.size / (1024 * 1024);

      // hard block
      if (sizeMB > this.hardLimitMB) {
        toast(
          this.$t("image-size-error", {
            size: sizeMB.toFixed(1),
            max: this.hardLimitMB
          }),
          { type: "error" }
        );
        return;
      }

      const isGif = file.type === "image/gif";
      let finalFile = file;

      try {
        if (!isGif && file.size > 1 * 1024 * 1024) {
          const compressedBlob = await imageCompression(file, {
            maxSizeMB: this.maxSizeMB,
            maxWidthOrHeight: this.maxWidthOrHeight,
            useWebWorker: true,
            initialQuality: this.initialQuality,
            fileType: file.type, // keep same mime type (png stays png, jpeg stays jpeg)
          });
          // Wrap blob back into a File so name/type look like original
          finalFile = new File([compressedBlob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          }); 
        }
      } catch (err) {
        console.error("Compression failed, using original file:", err);
        finalFile = file;
      }

      const finalMB = finalFile.size / (1024 * 1024);

      // emit file up
      this.$emit("update:value", finalFile);

      // create blob preview locally and emit previewUrl up so parent can store it
      const previewUrl = URL.createObjectURL(finalFile);
      this.internalPreviewUrl = previewUrl;
      this.$emit("update:previewUrl", previewUrl);

      if (finalFile !== file) {
        toast(
          this.$t("image-compressed", {
            before: sizeMB.toFixed(1),
            after: finalMB.toFixed(1)
          }),
          { type: "info" }
        );
      }
    },

    removeFile() {
      this.fileName = "";
      this.$emit("update:value", null);

      // if we created a blob, revoke it
      this.revokeBlobPreview();

      // tell parent to clear its previewUrl too (important)
      this.$emit("update:previewUrl", "");
    },
  },
};
</script>

<style scoped>
/* ===== Base layout ===== */
.util-input {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Token variables (controlled by size classes) */
.util-input {
  --label-fs: 20px;
  --in-h: 60px; /* input container height */
  --in-px: 16px; /* horizontal padding */
  --in-gap: 20px; /* gap between icon and field */
  --field-fs: 22px; /* input/textarea/dropdown font-size */
  --icon-h: 36px; /* icon height */
  --cb-size: 18px; /* checkbox size */
  --cb-label-fs: 18px; /* checkbox label size */
  --helper-fs: 14px; /* helper text size */
}

.label {
  font-size: var(--label-fs);
  font-weight: 700;
  margin-bottom: 8px;
}

.input-container {
  display: flex;
  align-items: center;
  height: var(--in-h);
  border-radius: 8px;
  gap: var(--in-gap);
  padding: 0 var(--in-px);
  background-color: #ededed; /* overridden via inline boxBg */
  box-sizing: border-box;
}

.input-disabled {
  cursor: not-allowed;
  opacity: 0.85;
}
.input-disabled * {
  cursor: not-allowed;
}

/* Field elements */
.text-input,
textarea,
.dropdown {
  flex: 1;
  border: none;
  outline: none;
  font-size: var(--field-fs);
  background: none;
  font-family: "Roboto", sans-serif;
  width: 100%;
}

textarea {
  resize: none;
}
.dropdown:hover {
  cursor: pointer;
}

.icon {
  height: var(--icon-h);
  width: auto;
}

/* Helper text */
.helper-text {
  margin-top: 12px;
  font-size: var(--helper-fs);
  color: rgba(0, 0, 0, 0.8);
}

/* Checkbox */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.checkbox {
  width: var(--cb-size);
  height: var(--cb-size);
  accent-color: #3f72af;
}
.checkbox-label {
  font-size: var(--cb-label-fs);
  font-family: "Roboto", sans-serif;
  color: #333;
}
.checkbox:hover,
.checkbox-label:hover {
  cursor: pointer;
}

/* Password toggle icon */
.password-visibility-toggle {
  cursor: pointer;
  width: 20px;
  height: 20px;
}


/* ✅ File upload (now works in ALL breakpoints) */
.file-upload {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.file-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #000; /* keep as-is */
  color: #fff;      /* keep as-is */
  cursor: pointer;
  white-space: nowrap;

  /* responsive sizing (smaller on mobile) */
  padding: 6px 10px;
  font-size: 12px;
}

.file-button:hover {
  opacity: 0.92;
}

.file-name {
  flex: 1;
  font-size: var(--field-fs);
  font-family: "Roboto", sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Remove (X) */
.file-remove {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  background: transparent;
  color: rgba(0, 0, 0, 0.75);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.file-remove:hover {
  background: rgba(0, 0, 0, 0.06);
}

/* Preview */
.file-preview {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.file-preview img {
  width: 100%;
  max-width: 520px;
  border-radius: 8px;
  border: 1px solid rgba(153, 153, 153, 0.5);
}

/* =============================== */
/*      SIZE VARIANTS (Mobile)     */
/* =============================== */

/* xs (extra small) */
.util-input.xs {
  --label-fs: 12px;
  --in-h: 28px;
  --in-px: 6px;
  --in-gap: 6px;
  --field-fs: 12px;
  --icon-h: 14px;
  --cb-size: 12px;
  --cb-label-fs: 12px;
  --helper-fs: 11px;
}

/* sm (mobile defaults) */
.util-input.sm {
  --label-fs: 14px;
  --in-h: 36px;
  --in-px: 8px;
  --in-gap: 8px;
  --field-fs: 14px;
  --icon-h: 18px;
  --cb-size: 14px;
  --cb-label-fs: 14px;
  --helper-fs: 12px;
}

/* md (mobile defaults) */
.util-input.md {
  --label-fs: 18px;
  --in-h: 48px;
  --in-px: 12px;
  --in-gap: 12px;
  --field-fs: 18px;
  --icon-h: 28px;
  --cb-size: 16px;
  --cb-label-fs: 16px;
  --helper-fs: 13px;
}

/* lg (mobile defaults) */
.util-input.lg {
  --label-fs: 20px;
  --in-h: 60px;
  --in-px: 16px;
  --in-gap: 20px;
  --field-fs: 22px;
  --icon-h: 36px;
  --cb-size: 18px;
  --cb-label-fs: 18px;
  --helper-fs: 14px;
}

.util-input.xs .file-button {
  padding: 4px 8px;
  font-size: 11px;
}

.util-input.sm .file-button {
  padding: 5px 9px;
  font-size: 12px;
}

.util-input.md .file-button {
  padding: 6px 10px;
  font-size: 13px;
}

.util-input.lg .file-button {
  padding: 8px 14px;
  font-size: 14px;
}

/* ================================== */
/*   ≥ 768px (Tablet) refinements     */
/* ================================== */
@media (min-width: 768px) {
  .util-input.xs {
    --label-fs: 13px;
    --in-h: 32px;
    --in-px: 8px;
    --in-gap: 8px;
    --field-fs: 13px;
    --icon-h: 16px;
    --cb-size: 13px;
    --cb-label-fs: 13px;
    --helper-fs: 11px;
  }

  .util-input.sm {
    --label-fs: 15px;
    --in-h: 40px;
    --in-px: 10px;
    --in-gap: 10px;
    --field-fs: 15px;
    --icon-h: 20px;
    --cb-size: 15px;
    --cb-label-fs: 15px;
  }

  .util-input.md {
    --label-fs: 19px;
    --in-h: 52px;
    --in-px: 14px;
    --in-gap: 14px;
    --field-fs: 19px;
    --icon-h: 30px;
    --cb-size: 17px;
    --cb-label-fs: 17px;
  }

  .util-input.lg {
    --label-fs: 22px;
    --in-h: 64px;
    --in-px: 18px;
    --in-gap: 20px;
    --field-fs: 24px;
    --icon-h: 38px;
    --cb-size: 18px;
    --cb-label-fs: 18px;
  }
}

@media (min-width: 768px) {
  .file-upload {
    gap: 12px;
  }
  .file-button {
    padding: 7px 12px;
    font-size: 13px;
  }
  .file-remove {
    width: 30px;
    height: 30px;
    font-size: 22px;
  }
}

/* ================================== */
/*   ≥ 1024px (Desktop) refinements   */
/* ================================== */
@media (min-width: 1024px) {
  .util-input.xs {
    --label-fs: 14px;
    --in-h: 34px;
    --in-px: 10px;
    --in-gap: 10px;
    --field-fs: 14px;
    --icon-h: 18px;
    --cb-size: 14px;
    --cb-label-fs: 14px;
    --helper-fs: 12px;
  }

  .util-input.sm {
    --label-fs: 16px;
    --in-h: 42px;
    --in-px: 12px;
    --in-gap: 12px;
    --field-fs: 16px;
    --icon-h: 22px;
    --cb-size: 16px;
    --cb-label-fs: 16px;
  }

  .util-input.md {
    --label-fs: 20px;
    --in-h: 56px;
    --in-px: 16px;
    --in-gap: 16px;
    --field-fs: 20px;
    --icon-h: 32px;
    --cb-size: 18px;
    --cb-label-fs: 18px;
  }

  .util-input.lg {
    --label-fs: 22px;
    --in-h: 68px;
    --in-px: 20px;
    --in-gap: 22px;
    --field-fs: 26px;
    --icon-h: 40px;
    --cb-size: 20px;
    --cb-label-fs: 18px;
  }
  
  .file-button {
      padding: 8px 14px;
      font-size: 14px;
  }
}
</style>
